// src/features/Planning/components/Step17StaircaseDimentions.jsx
import {
  VStack,
  Text,
  Flex,
  Button,
  Box,
  Input,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  IconButton,
  Badge,
} from "@chakra-ui/react";
import { FiPlay, FiAlertCircle, FiCheckCircle, FiAlertTriangle } from "react-icons/fi";
import StaircaseDimentionsImg from "../../../../assets/Planning/StaircaseDimentions/StaircaseDimentions.png";
import RiserandThread from "../../../../assets/Planning/StaircaseDimentions/RiserandThread.mp4";

const RECOMMENDED_TREAD = "10";
const RECOMMENDED_RISER = "7";

const TREAD_MIN = 10;       // below this → Too Short (blocked)
const TREAD_LONG = 12;      // 10–12 → Good; 12–14 → Long (warning, allowed)
const TREAD_TOO_LONG = 14;  // above this → Not Preferable / Too Long (warning, allowed)

const RISER_GOOD_MAX = 7;   // 1–7 → Good
const RISER_MAX = 8;        // 7–8 → Not Preferred; above 8 → Too High (blocked)

// ── Tread status helper ───────────────────────────────────────────────────────
function getTreadStatus(val) {
  if (!val || val <= 0) return null;
  if (val < TREAD_MIN) return "too_short";       // blocked
  if (val <= TREAD_LONG) return "good";
  if (val <= TREAD_TOO_LONG) return "long";      // warning, allowed
  return "too_long";                              // warning, allowed
}

// ── Riser status helper ───────────────────────────────────────────────────────
function getRiserStatus(val) {
  if (!val || val <= 0) return null;
  if (val <= RISER_GOOD_MAX) return "good";
  if (val <= RISER_MAX) return "not_preferred";  // warning, allowed
  return "too_high";                             // blocked
}

// ── Status configs ────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  too_short:     { color: "red",    icon: "alert",    label: "Too Short",       border: "red.400",    bg: "red.50",    textColor: "red.600"    },
  good:          { color: "green",  icon: "check",    label: "Good",            border: "green.400",  bg: "green.50",  textColor: "green.600"  },
  long:          { color: "orange", icon: "triangle", label: "Long",            border: "orange.400", bg: "orange.50", textColor: "orange.600" },
  too_long:      { color: "red",    icon: "alert",    label: "Not Preferable",  border: "red.400",    bg: "red.50",    textColor: "red.600"    },
  not_preferred: { color: "orange", icon: "triangle", label: "Not Preferred",   border: "orange.400", bg: "orange.50", textColor: "orange.600" },
  too_high:      { color: "red",    icon: "alert",    label: "Too High",        border: "red.400",    bg: "red.50",    textColor: "red.600"    },
};

function StatusIcon({ type, size = 12 }) {
  if (type === "check")    return <FiCheckCircle   size={size} color="#38A169" />;
  if (type === "triangle") return <FiAlertTriangle size={size} color="#DD6B20" />;
  return <FiAlertCircle size={size} color="#E53E3E" />;
}

export default function Step17StaircaseDimentions({
  formData,
  setFormData,
  onNext,
  onBack,
  isLastStep,
  onSubmit,
  viewMode,
}) {
  const tread = formData?.tread ?? RECOMMENDED_TREAD;
  const riser = formData?.riser ?? RECOMMENDED_RISER;

  const treadNum = parseFloat(tread) || 0;
  const riserNum = parseFloat(riser) || 0;

  const isTreadChanged = tread !== RECOMMENDED_TREAD;
  const isRiserChanged = riser !== RECOMMENDED_RISER;

  const treadStatus = getTreadStatus(treadNum);
  const riserStatus = getRiserStatus(riserNum);

  const treadConfig = treadStatus ? STATUS_CONFIG[treadStatus] : null;
  const riserConfig = riserStatus ? STATUS_CONFIG[riserStatus] : null;

  // Blocked = form invalid
  const treadBlocked = treadStatus === "too_short";
  const riserBlocked = riserStatus === "too_high";

  const isFormValid =
    treadNum > 0 && riserNum > 0 && !treadBlocked && !riserBlocked;

  const { isOpen: isVideoOpen, onOpen: onVideoOpen, onClose: onVideoClose } = useDisclosure();
  const { isOpen: isImageOpen, onOpen: onImageOpen, onClose: onImageClose } = useDisclosure();

  const handleChange = (field, value) => {
    // Allow free typing (empty, partial digits) — validation happens via status
    // Only block if user finishes typing a complete number below TREAD_MIN
    // i.e. don't block "1" while they're still typing "10", "11", "12" etc.
    if (field === "tread") {
      // Block only if value has 2+ digits and is still below min
      // This lets user clear fully (empty string) or type digit by digit
      const num = parseFloat(value);
      if (value.length >= 2 && !isNaN(num) && num < TREAD_MIN) return;
    }
    setFormData({ ...formData, [field]: value });
  };

  const handleRestore = (field, recommendedValue) => {
    setFormData({ ...formData, [field]: recommendedValue });
  };

  const handleNext = () => {
    if (!viewMode && !isFormValid) return;
    if (isLastStep) onSubmit();
    else onNext();
  };

  // ── Error/warning message text ────────────────────────────────────────────────
  const getTreadMessage = () => {
    if (treadStatus === "too_short")  return `Tread must be at least ${TREAD_MIN}" — too short is unsafe`;
    if (treadStatus === "long")       return `Tread is on the longer side (>${TREAD_LONG}") — still acceptable`;
    if (treadStatus === "too_long")   return `Tread over ${TREAD_TOO_LONG}" is not preferable — may waste space`;
    return null;
  };

  const getRiserMessage = () => {
    if (riserStatus === "not_preferred") return `Riser between ${RISER_GOOD_MAX}"–${RISER_MAX}" is not preferred — harder to climb`;
    if (riserStatus === "too_high")      return `Riser must be ${RISER_MAX}" or less — too high is unsafe`;
    return null;
  };

  // ── Border color ──────────────────────────────────────────────────────────────
  const treadBorder = treadConfig?.border ?? "gray.300";
  const riserBorder = riserConfig?.border ?? "gray.300";

  // ── Input background ──────────────────────────────────────────────────────────
  const treadBg = treadConfig?.bg ?? "white";
  const riserBg = riserConfig?.bg ?? "white";

  return (
    <Box
      h="600px"
      w="100%"
      maxW="400px"
      mx="auto"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      px={4}
      py={6}
    >
      {/* Content Area */}
      <Box
        flex="1"
        overflowY="auto"
        pr={1}
        css={{
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-thumb": { background: "#cbd5e0", borderRadius: "8px" },
          "& *": { pointerEvents: viewMode ? "none" : "auto" },
        }}
      >
        <VStack spacing={4} align="stretch">

          {/* Heading */}
          <Text fontSize="17px" fontWeight="600" color="gray.800" textAlign="center">
            Staircase Dimensions
          </Text>

          {/* Image Box */}
          <Box
            bg="gray.50"
            borderRadius="12px"
            display="flex"
            flexDirection="column"
            alignItems="center"
            p={4}
            cursor="pointer"
            onClick={onImageOpen}
            transition="all 0.2s ease"
            _hover={{ boxShadow: "0 4px 12px rgba(0,0,0,0.1)", transform: "scale(1.02)" }}
          >
            <Box
              border="1px solid" borderColor="gray.200" borderRadius="10px"
              overflow="hidden" mb={2} p={2} bg="white"
              display="flex" justifyContent="center" alignItems="center"
              w="100%" h="160px" position="relative"
            >
              <Image src={StaircaseDimentionsImg} alt="Staircase" maxHeight="140px" maxWidth="100%" objectFit="contain" />
              <Box position="absolute" bottom="2" right="2" bg="blackAlpha.700" color="white" px={2} py={1} borderRadius="md" fontSize="11px" fontWeight="500">
                Click to enlarge
              </Box>
            </Box>
            <IconButton
              icon={<FiPlay size={18} />}
              colorScheme="cyan" size="md" borderRadius="full" mt={2}
              onClick={(e) => { e.stopPropagation(); onVideoOpen(); }}
              aria-label="Play tutorial video"
              boxShadow="md"
              _hover={{ transform: "scale(1.1)" }}
              transition="all 0.2s ease"
            />
          </Box>

          {/* ── Rule Banner ── */}
          <Box bg="blue.50" border="1px solid" borderColor="blue.200" borderRadius="10px" px={4} py={3}>
            <Text fontSize="12px" fontWeight="700" color="blue.700" mb={2}>
              📐 Standard Rules
            </Text>
            <VStack spacing={1} align="stretch">
              <Flex align="center" gap={2}>
                <Box w="6px" h="6px" borderRadius="full" bg="green.400" flexShrink={0} />
                <Text fontSize="12px" color="blue.700">
                  <Text as="span" fontWeight="700">Tread</Text> — min{" "}
                  <Text as="span" fontWeight="700">{TREAD_MIN}"</Text>, ideal up to{" "}
                  <Text as="span" fontWeight="700">{TREAD_LONG}"</Text>
                </Text>
              </Flex>
              <Flex align="center" gap={2}>
                <Box w="6px" h="6px" borderRadius="full" bg="orange.400" flexShrink={0} />
                <Text fontSize="12px" color="blue.700">
                  <Text as="span" fontWeight="700">Riser</Text> — ideal up to{" "}
                  <Text as="span" fontWeight="700">{RISER_GOOD_MAX}"</Text>, max{" "}
                  <Text as="span" fontWeight="700">{RISER_MAX}"</Text>
                </Text>
              </Flex>
            </VStack>
          </Box>

          {/* ── Inputs block ── */}
          <Box bg="white" borderRadius="12px" border="1px solid" borderColor="gray.200" p={4}>
            <VStack spacing={5} align="stretch">

              {/* ── TREAD ── */}
              <Box>
                <Flex align="center" justify="space-between" mb={1}>
                  <Text fontWeight="700" fontSize="14px" color="gray.700">
                    Tread
                    <Text as="span" fontSize="12px" fontWeight="400" color="gray.500" ml={1}>(step depth)</Text>
                  </Text>
                  {!isTreadChanged ? (
                    <Badge colorScheme="green" fontSize="10px" borderRadius="full" px={2}>
                      ✓ Recommended
                    </Badge>
                  ) : (
                    <Flex align="center" gap={2}>
                      <Badge colorScheme="orange" fontSize="10px" borderRadius="full" px={2}>
                        Rec: {RECOMMENDED_TREAD}"
                      </Badge>
                      <Text fontSize="10px" color="cyan.500" fontWeight="600" cursor="pointer" textDecoration="underline"
                        onClick={() => handleRestore("tread", RECOMMENDED_TREAD)}>
                        Restore
                      </Text>
                    </Flex>
                  )}
                </Flex>

                {/* Range guide */}
                <Flex align="center" gap={1} mb={2}>
                  <Box w="4px" h="4px" borderRadius="full" bg="green.400" />
                  <Text fontSize="11px" color="gray.500">
                    Min: {TREAD_MIN}" · Good: {TREAD_MIN}"–{TREAD_LONG}" · Long: {TREAD_LONG}"–{TREAD_TOO_LONG}" · Not Preferable: &gt;{TREAD_TOO_LONG}"
                  </Text>
                </Flex>

                <Flex align="center" gap={3}>
                  <Input
                    value={tread}
                    onChange={(e) => handleChange("tread", e.target.value)}
                    placeholder="10"
                    h="44px"
                    w="88px"
                    type="number"
                    min={TREAD_MIN}
                    borderRadius="8px"
                    fontSize="18px"
                    fontWeight="700"
                    borderColor={treadBorder}
                    borderWidth="2px"
                    textAlign="center"
                    color={treadConfig?.textColor ?? "gray.800"}
                    bg={treadBg}
                    _hover={{ borderColor: "cyan.400" }}
                    _focus={{ borderColor: "cyan.500", boxShadow: "0 0 0 1px var(--chakra-colors-cyan-500)" }}
                  />
                  <Box>
                    <Text color="gray.600" fontSize="14px" fontWeight="500">inches</Text>
                    {treadConfig && (
                      <Flex align="center" gap={1} mt={0.5}>
                        <StatusIcon type={treadConfig.icon} />
                        <Text fontSize="11px" color={treadConfig.textColor} fontWeight="600">
                          {treadConfig.label}
                        </Text>
                      </Flex>
                    )}
                  </Box>
                </Flex>

                {/* Message banner */}
                {getTreadMessage() && (
                  <Box
                    bg={treadBg}
                    border="1px solid"
                    borderColor={treadBorder}
                    borderRadius="6px"
                    px={3} py={2} mt={2}
                  >
                    <Flex align="center" gap={2}>
                      <StatusIcon type={treadConfig.icon} size={13} />
                      <Text fontSize="12px" color={treadConfig.textColor} fontWeight="600">
                        {getTreadMessage()}
                      </Text>
                    </Flex>
                  </Box>
                )}
              </Box>

              {/* Divider */}
              <Box h="1px" bg="gray.100" />

              {/* ── RISER ── */}
              <Box>
                <Flex align="center" justify="space-between" mb={1}>
                  <Text fontWeight="700" fontSize="14px" color="gray.700">
                    Riser
                    <Text as="span" fontSize="12px" fontWeight="400" color="gray.500" ml={1}>(step height)</Text>
                  </Text>
                  {!isRiserChanged ? (
                    <Badge colorScheme="green" fontSize="10px" borderRadius="full" px={2}>
                      ✓ Recommended
                    </Badge>
                  ) : (
                    <Flex align="center" gap={2}>
                      <Badge colorScheme="orange" fontSize="10px" borderRadius="full" px={2}>
                        Rec: {RECOMMENDED_RISER}"
                      </Badge>
                      <Text fontSize="10px" color="cyan.500" fontWeight="600" cursor="pointer" textDecoration="underline"
                        onClick={() => handleRestore("riser", RECOMMENDED_RISER)}>
                        Restore
                      </Text>
                    </Flex>
                  )}
                </Flex>

                {/* Range guide */}
                <Flex align="center" gap={1} mb={2}>
                  <Box w="4px" h="4px" borderRadius="full" bg="orange.400" />
                  <Text fontSize="11px" color="gray.500">
                    Good: ≤{RISER_GOOD_MAX}" · Not Preferred: {RISER_GOOD_MAX}"–{RISER_MAX}" · Too High: &gt;{RISER_MAX}"
                  </Text>
                </Flex>

                <Flex align="center" gap={3}>
                  <Input
                    value={riser}
                    onChange={(e) => handleChange("riser", e.target.value)}
                    placeholder="7"
                    h="44px"
                    w="88px"
                    type="number"
                    min="1"
                    max={RISER_MAX}
                    borderRadius="8px"
                    fontSize="18px"
                    fontWeight="700"
                    borderColor={riserBorder}
                    borderWidth="2px"
                    textAlign="center"
                    color={riserConfig?.textColor ?? "gray.800"}
                    bg={riserBg}
                    _hover={{ borderColor: "cyan.400" }}
                    _focus={{ borderColor: "cyan.500", boxShadow: "0 0 0 1px var(--chakra-colors-cyan-500)" }}
                  />
                  <Box>
                    <Text color="gray.600" fontSize="14px" fontWeight="500">inches</Text>
                    {riserConfig && (
                      <Flex align="center" gap={1} mt={0.5}>
                        <StatusIcon type={riserConfig.icon} />
                        <Text fontSize="11px" color={riserConfig.textColor} fontWeight="600">
                          {riserConfig.label}
                        </Text>
                      </Flex>
                    )}
                  </Box>
                </Flex>

                {/* Message banner */}
                {getRiserMessage() && (
                  <Box
                    bg={riserBg}
                    border="1px solid"
                    borderColor={riserBorder}
                    borderRadius="6px"
                    px={3} py={2} mt={2}
                  >
                    <Flex align="center" gap={2}>
                      <StatusIcon type={riserConfig.icon} size={13} />
                      <Text fontSize="12px" color={riserConfig.textColor} fontWeight="600">
                        {getRiserMessage()}
                      </Text>
                    </Flex>
                  </Box>
                )}
              </Box>

            </VStack>
          </Box>

        </VStack>
      </Box>

      {/* Image Zoom Modal */}
      <Modal isOpen={isImageOpen} onClose={onImageClose} size={{ base: "full", md: "4xl" }} isCentered>
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent mx={{ base: 0, md: 4 }} my={{ base: 0, md: 4 }} maxH={{ base: "100vh", md: "90vh" }} borderRadius={{ base: 0, md: "12px" }}>
          <ModalHeader fontSize="16px" fontWeight="600" color="gray.700" bg="white">
            Staircase Dimensions - Enlarged View
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody p={{ base: 2, md: 4 }} display="flex" alignItems="center" justifyContent="center" bg="gray.50" overflowY="auto">
            <Image src={StaircaseDimentionsImg} alt="Staircase Dimensions - Enlarged" w="100%" h="auto" maxH={{ base: "calc(100vh - 80px)", md: "70vh" }} objectFit="contain" borderRadius="8px" />
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Video Modal */}
      <Modal isOpen={isVideoOpen} onClose={onVideoClose} size="md" isCentered>
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent mx={4} borderRadius="12px" maxW="400px">
          <ModalHeader fontSize="16px" fontWeight="600" color="gray.700">
            Riser & Tread - Tutorial Video
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={4}>
            <Box position="relative" width="100%" bg="black" borderRadius="8px" overflow="hidden">
              <video controls autoPlay
                style={{ width: "100%", height: "500px", maxHeight: "70vh", objectFit: "contain", display: "block" }}
                src={RiserandThread}>
                Your browser does not support the video tag.
              </video>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Fixed Navigation Buttons */}
      <Flex justify="space-between" gap={3} mt={4} flexShrink={0}>
        <Button
          onClick={onBack}
          variant="solid" bg="white" color="cyan.600"
          border="1px solid" borderColor="cyan.500"
          borderRadius="6px" minW="100px" h="40px"
          fontWeight="500" fontSize="14px"
          _hover={{ bg: "cyan.50", borderColor: "cyan.600" }}
        >
          ← Previous
        </Button>
        <Button
          onClick={handleNext}
          isDisabled={viewMode ? false : !isFormValid}
          variant="solid"
          bg={(viewMode || isFormValid) ? "cyan.500" : "gray.300"}
          color={(viewMode || isFormValid) ? "white" : "gray.500"}
          border="none" borderRadius="6px"
          minW="100px" h="40px"
          fontWeight="500" fontSize="14px" ml="auto"
          _hover={{ bg: (viewMode || isFormValid) ? "cyan.600" : "gray.300" }}
          _disabled={{ cursor: "not-allowed" }}
        >
          {isLastStep ? "Submit" : "Next →"}
        </Button>
      </Flex>
    </Box>
  );
}